import axios, { type AxiosInstance } from 'axios';
import { getSessionToken } from '@descope/react-sdk';
import { config } from '../config/env';

// Active tenant snapshot — initialized from localStorage immediately so the
// very first request (fired before React effects run) already carries the header.
// Updated by TenantContext on every tenant switch / logout.
function readStoredTenantId(): number | null {
  try {
    const raw = localStorage.getItem('brandlight.tenantId');
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

const tenantSnapshot: { id: number | null } = { id: readStoredTenantId() };

/** Called by TenantContext on login / tenant switch so the interceptor stays in sync. */
export function setActiveTenantId(id: number | null) {
  tenantSnapshot.id = id;
}

/**
 * Thrown by the request interceptor when a tenant-scoped request is attempted
 * with no active customer selected. The request is NOT sent — performing it
 * would be a logical error (the backend rejects it with 403 anyway).
 */
export class MissingTenantError extends Error {
  constructor(url?: string) {
    super(`No customer selected — request${url ? ` to ${url}` : ''} was not performed.`);
    this.name = 'MissingTenantError';
  }
}

/**
 * Paths that work WITHOUT a tenant context. Mirror of the backend's
 * `useTenantless` registrations in main.ts: /hello-auth, /tenants and the
 * common module's global catalogs (+ /prompt-funnel-stages, a global
 * catalog served by the prompt module). Everything else requires
 * X-Tenant-Id and is blocked client-side until a customer is selected.
 */
const TENANTLESS_PATHS = [
  '/hello-auth',
  '/tenants',
  // common module (global catalogs)
  '/ai-models',
  '/ai-model-providers',
  '/ai-model-ai-providers',
  '/brands',
  '/brand-domains',
  '/brand-promps',
  '/categories',
  '/domain-categories',
  '/domains',
  '/geo-locations',
  '/organizations',
  '/personas',
  '/products',
  '/search-phrases',
  '/topics',
  // global catalog served by the prompt module
  '/prompt-funnel-stages',
];

function requiresTenant(url: string | undefined): boolean {
  if (!url) return true;
  const path = url.startsWith('/') ? url : `/${url}`;
  return !TENANTLESS_PATHS.some(
    (p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`),
  );
}

let onUnauthorized: (() => void) | null = null;

/** Hook a global handler for 401s so AuthContext can sign the user out. */
export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

export const api: AxiosInstance = axios.create({
  baseURL: config.apiBaseUrl,
});

api.interceptors.request.use((req) => {
  const token = getSessionToken();
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantSnapshot.id !== null) {
    req.headers['X-Tenant-Id'] = String(tenantSnapshot.id);
  } else if (requiresTenant(req.url)) {
    // Tenant-scoped request with no customer selected: performing it would
    // be a logical error — block it before it leaves the browser.
    return Promise.reject(new MissingTenantError(req.url));
  }
  return req;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err?.response?.status === 401 && onUnauthorized) {
      onUnauthorized();
    }
    return Promise.reject(err);
  },
);

/**
 * Extracts the most useful error message from an axios error and **always
 * returns a string** — callers pass the result straight into React, so
 * returning an object would crash the render.
 */
export function extractErrorMessage(err: unknown): string {
  if (err instanceof MissingTenantError) {
    return 'No customer selected. Select a customer in the sidebar first.';
  }
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | {
          message?: string;
          error?: string | { code?: string; message?: string };
          errors?: Array<{ message?: string }>;
        }
      | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
      return data.error.message;
    }
    const firstFieldError = data?.errors?.[0]?.message;
    if (typeof firstFieldError === 'string') return firstFieldError;
    return err.message ?? 'Request failed';
  }
  return err instanceof Error ? err.message : 'Unknown error';
}
